/**
 * RACEIT.CC B2B dealer application — client-side hardening.
 *
 * A Shopify theme cannot run custom server-side validation (the contact form
 * posts to Shopify's own endpoint), so this layer provides: real field
 * validation (required / email / phone / VAT) on blur + submit, first-invalid
 * focus, a "same as shipping" billing toggle, a honeypot, and a submit-timing
 * trap against bots. Shopify still validates the email and applies its own spam
 * protection server-side.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-b2b-application]');
  if (!root) return;
  var form = root.closest('form');
  if (!form) return;

  // With JS present, our inline validation is the single source of truth.
  // (Without JS, the HTML5 required/type constraints remain as a fallback.)
  form.setAttribute('novalidate', 'novalidate');

  var MSG = {
    required: root.getAttribute('data-msg-required') || 'This field is required.',
    email: root.getAttribute('data-msg-email') || 'Enter a valid email address.',
    phone: root.getAttribute('data-msg-phone') || 'Enter a valid phone number.',
    vat: root.getAttribute('data-msg-vat') || 'Enter a valid VAT / Tax ID.',
    radio: root.getAttribute('data-msg-radio') || 'Please choose an option.'
  };

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var PHONE_RE = /^[+()\-\s.\/\d]{6,}$/;
  var VAT_RE = /^[A-Za-z0-9\-\s.]{6,20}$/;

  var startedAt = Date.now();
  var MIN_MS = 2000;

  /* ---- Billing "same as shipping" ---- */
  var sameChk = root.querySelector('[data-b2b-same-as-shipping]');
  var billing = root.querySelector('[data-b2b-billing]');
  var sameFlag = root.querySelector('[data-b2b-same-flag]');

  function billFields() {
    return Array.prototype.slice.call(root.querySelectorAll('[data-bill-key]'));
  }

  function clearBilling() {
    billFields().forEach(function (el) {
      el.value = '';
      clearError(el);
    });
  }

  // Copy each shipping value into its billing counterpart so the notification
  // email always carries a complete billing address, even when "same as
  // shipping" is on. Keys are paired via data-ship-key / data-bill-key.
  function copyShippingToBilling() {
    var map = {};
    root.querySelectorAll('[data-ship-key]').forEach(function (el) {
      map[el.getAttribute('data-ship-key')] = el.value;
    });
    billFields().forEach(function (el) {
      var key = el.getAttribute('data-bill-key');
      if (key in map) el.value = map[key];
    });
  }

  function syncBilling() {
    if (!sameChk) return;
    var same = sameChk.checked;
    if (billing) billing.hidden = same;
    // Distinct billing address: start clean. "Same": leave fields for the
    // submit-time copy so the buyer never has to retype their address.
    if (!same) clearBilling();
    if (sameFlag) sameFlag.value = same ? 'Sí' : 'No';
  }

  /* ---- Error helpers ---- */
  function fieldWrap(el) {
    return el.closest('.native-b2b__field') || el.closest('.native-b2b__radio-group') || el.parentElement;
  }

  function setError(el, msg) {
    var wrap = fieldWrap(el);
    if (!wrap) return;
    var err = wrap.querySelector('.native-b2b__field-error');
    if (!err) {
      err = document.createElement('span');
      err.className = 'native-b2b__field-error';
      err.setAttribute('role', 'alert');
      wrap.appendChild(err);
    }
    err.textContent = msg;
    el.setAttribute('aria-invalid', 'true');
    wrap.classList.add('has-error');
  }

  function clearError(el) {
    var wrap = fieldWrap(el);
    if (!wrap) return;
    var err = wrap.querySelector('.native-b2b__field-error');
    if (err) err.textContent = '';
    el.removeAttribute('aria-invalid');
    wrap.classList.remove('has-error');
  }

  function isHidden(el) {
    return el.disabled || el.type === 'hidden' || !!el.closest('[hidden]');
  }

  function validateField(el) {
    if (isHidden(el)) return true;
    var val = (el.value || '').trim();
    if (el.hasAttribute('required') && !val) { setError(el, MSG.required); return false; }
    if (!val) { clearError(el); return true; }
    if (el.hasAttribute('data-b2b-email') && !EMAIL_RE.test(val)) { setError(el, MSG.email); return false; }
    if (el.hasAttribute('data-b2b-phone') && !PHONE_RE.test(val)) { setError(el, MSG.phone); return false; }
    if (el.hasAttribute('data-b2b-vat') && !VAT_RE.test(val)) { setError(el, MSG.vat); return false; }
    clearError(el);
    return true;
  }

  function validatableFields() {
    return Array.prototype.slice.call(
      root.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), select')
    );
  }

  function validateRadios() {
    var group = root.querySelector('[data-b2b-radio-required]');
    if (!group) return { ok: true };
    var radios = group.querySelectorAll('input[type="radio"]');
    var checked = Array.prototype.some.call(radios, function (r) { return r.checked; });
    if (!checked) { setError(radios[0], MSG.radio); return { ok: false, el: radios[0] }; }
    clearError(radios[0]);
    return { ok: true };
  }

  /* ---- Wire up ---- */
  if (sameChk) {
    sameChk.addEventListener('change', syncBilling);
    syncBilling();
  }

  validatableFields().forEach(function (el) {
    el.addEventListener('blur', function () { validateField(el); });
  });

  /* ---- Automatic company creation ---- */
  // When the section setting "Automatic company creation URL" is filled, the
  // same answers are also sent to the Apps Script web app in
  // integrations/b2b-application-google-apps-script, which creates the Shopify
  // company awaiting approval. It is a beacon, so it never delays or blocks the
  // contact form, whose store email stays the fallback if the web app fails.
  var endpoint = (root.getAttribute('data-b2b-endpoint') || '').trim();

  function valueOf(el) {
    return el ? String(el.value || '').trim() : '';
  }

  function addressFrom(attr) {
    var address = {};
    root.querySelectorAll('[' + attr + ']').forEach(function (el) {
      address[el.getAttribute(attr)] = valueOf(el);
    });
    return address;
  }

  function buildApplication() {
    var regime = root.querySelector('[data-b2b-radio-required] input[type="radio"]:checked');
    return {
      v: 1,
      hp: valueOf(form.querySelector('[name="b2b_hp"]')),
      elapsedMs: Date.now() - startedAt,
      locale: root.getAttribute('data-b2b-locale') || document.documentElement.lang || '',
      company: valueOf(form.querySelector('[name="contact[Empresa]"]')),
      vat: valueOf(root.querySelector('[data-b2b-vat]')),
      email: valueOf(root.querySelector('[data-b2b-email]')),
      taxRegime: regime ? regime.value : '',
      billingSameAsShipping: !sameChk || sameChk.checked,
      shipping: addressFrom('data-ship-key'),
      billing: addressFrom('data-bill-key')
    };
  }

  function sendToAutomation() {
    if (!/^https:\/\//.test(endpoint)) return;
    var body = JSON.stringify(buildApplication());
    // text/plain keeps this a CORS "simple" request with no preflight, which
    // an Apps Script web app cannot answer.
    //
    // fetch(..., keepalive:true) is the primary path, not sendBeacon: an
    // Apps Script /exec URL always answers with a 302 to a one-time content
    // URL, and navigator.sendBeacon() returning true only means the browser
    // *queued* the request — it does not confirm delivery, and in practice
    // beacons to a redirecting endpoint are silently dropped by some
    // browsers/extensions with no way to detect the failure. fetch follows
    // the redirect like a normal request and keepalive survives page unload
    // the same way a beacon does, so it is the reliable choice here.
    try {
      fetch(endpoint, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: body });
      return;
    } catch (err) { /* fall back to sendBeacon (e.g. no fetch support) */ }
    try {
      navigator.sendBeacon && navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
    } catch (err) { /* the contact form email still reaches the store */ }
  }

  form.addEventListener('submit', function (e) {
    // Honeypot: real users never fill this (name has no contact[] prefix, so it
    // is never emailed either).
    var hp = form.querySelector('[name="b2b_hp"]');
    if (hp && hp.value.trim() !== '') { e.preventDefault(); return; }

    // Timing trap: humans don't complete this form in under 2s. Re-clicking a
    // moment later (time elapses) lets a genuine user through.
    if (Date.now() - startedAt < MIN_MS) { e.preventDefault(); return; }

    // Mirror shipping into billing before the POST when "same as shipping".
    if (sameChk && sameChk.checked) copyShippingToBilling();

    var firstInvalid = null;
    validatableFields().forEach(function (el) {
      if (!validateField(el) && !firstInvalid) firstInvalid = el;
    });
    var radios = validateRadios();
    if (!radios.ok && !firstInvalid) firstInvalid = radios.el;

    if (firstInvalid) {
      e.preventDefault();
      firstInvalid.focus();
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Valid: hand the answers to the company-creation web app, then let the
    // contact form submit as usual so the store email is unchanged.
    sendToAutomation();
  });
})();
